/**
 * M4b-redux Criminal Justice — Rescue Runner Modal.
 *
 * Two-tab modal: STEALTH (Runner-Picker + DC preview) | BRIBE (Judge cash).
 * Follows the Epitaph-Modal a11y/focus-trap/ESC/Click-outside pattern from M2.
 *
 * Usage:
 *   showRescueModal({ arrestedRunner, rescuerPool, onSubmit })
 *
 * Where `arrestedRunner` is the RunnerDTO with arrest metadata, `rescuerPool` is
 * an array of eligible rescuer RunnerDTOs (active, non-arrested, non-manager),
 * and `onSubmit(mode, data)` is the callback invoked on Confirm.
 */
import { t } from '../i18n';
import type { RunnerDTO } from '../data/fixture';

/** Severity → Rescue DC (mirrors game_params.json criminal_justice). */
const RESCUE_DC: Record<string, number> = {
  petty: 30,
  moderate: 45,
  serious: 60,
  federal: 85,
};

export interface RescueModalOpts {
  arrestedRunner: RunnerDTO;
  rescuerPool: RunnerDTO[];
  /** Called when user confirms. Mode "stealth" → data.rescuerId; "bribe" → no data. */
  onSubmit: (mode: 'stealth' | 'bribe') => Promise<void>;
}

let activeOverlay: HTMLElement | null = null;

/** Internal helper to reset state between renders (used by tests). */
export function _resetRescueModal(): void {
  if (activeOverlay && activeOverlay.parentElement) {
    activeOverlay.parentElement.removeChild(activeOverlay);
  }
  activeOverlay = null;
}

/** Render and attach the rescue modal. Returns nothing — modal self-destroys on close. */
export function showRescueModal(opts: RescueModalOpts): void {
  _resetRescueModal();

  const severity = opts.arrestedRunner.arrestSeverity ?? 'petty';
  const isFederal = severity === 'federal';
  const dc = RESCUE_DC[severity] ?? 30;

  const overlay = document.createElement('div');
  overlay.id = 'rescue-modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', t('rescue.dialog_label'));
  Object.assign(overlay.style, {
    position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: '10000',
  });

  const panel = document.createElement('div');
  panel.className = 'rescue-panel';
  Object.assign(panel.style, {
    background: 'var(--bg-panel, #1a1a1f)', border: '1px solid var(--red, #e63946)',
    borderRadius: '6px', padding: '20px', minWidth: '460px', maxWidth: '600px',
    color: 'var(--text-hi, #eef)', fontFamily: 'var(--mono, monospace)',
  });

  // Header
  const title = document.createElement('div');
  title.style.cssText = 'font-size:15px;letter-spacing:1.5px;margin-bottom:10px;color:var(--red,#e63946);text-transform:uppercase';
  title.textContent = t('rescue.modal_title', { name: opts.arrestedRunner.name });
  panel.appendChild(title);

  const info = document.createElement('div');
  info.style.cssText = 'font-size:12px;margin-bottom:14px;color:var(--text-mid,#bbb)';
  info.innerHTML = `${t('rescue.severity_label')}: <strong>${severity.toUpperCase()}</strong> · DC ${dc}`;
  panel.appendChild(info);

  // Tabs
  const tabs = document.createElement('div');
  tabs.style.cssText = 'display:flex;gap:6px;margin-bottom:14px;border-bottom:1px solid var(--border,#333)';
  const stealthTab = document.createElement('button');
  stealthTab.className = 'rescue-tab rescue-tab-stealth active';
  stealthTab.textContent = t('rescue.tab_stealth');
  stealthTab.style.cssText = 'background:transparent;border:0;color:var(--cyan,#0f5);padding:8px 16px;cursor:pointer;font-family:inherit;border-bottom:2px solid var(--cyan,#0f5)';
  const bribeTab = document.createElement('button');
  bribeTab.className = 'rescue-tab rescue-tab-bribe';
  bribeTab.textContent = t('rescue.tab_bribe');
  bribeTab.style.cssText = 'background:transparent;border:0;color:var(--text-lo,#888);padding:8px 16px;cursor:pointer;font-family:inherit;border-bottom:2px solid transparent';
  if (isFederal) {
    bribeTab.disabled = true;
    bribeTab.style.opacity = '0.4';
    bribeTab.style.cursor = 'not-allowed';
    bribeTab.title = t('rescue.bribe_federal_blocked');
  }
  tabs.appendChild(stealthTab);
  tabs.appendChild(bribeTab);
  panel.appendChild(tabs);

  // Content area
  const content = document.createElement('div');
  content.className = 'rescue-content';
  content.style.cssText = 'min-height:140px;margin-bottom:14px';
  panel.appendChild(content);

  // --- Stealth tab content ---
  const renderStealthTab = () => {
    content.innerHTML = '';
    const pick = document.createElement('div');
    pick.style.cssText = 'font-size:12px;margin-bottom:8px;color:var(--text-mid,#bbb)';
    pick.textContent = t('rescue.pick_rescuer');
    content.appendChild(pick);

    const select = document.createElement('select');
    select.id = 'rescue-rescuer-select';
    select.style.cssText = 'width:100%;padding:6px 8px;background:var(--bg,#0c0c10);border:1px solid var(--border,#333);color:var(--text-hi,#eef);font-family:inherit;margin-bottom:10px';
    if (opts.rescuerPool.length === 0) {
      const opt = document.createElement('option');
      opt.textContent = t('rescue.no_rescuer_available');
      opt.disabled = true;
      select.appendChild(opt);
      select.disabled = true;
    }
    opts.rescuerPool.forEach(r => {
      const opt = document.createElement('option');
      opt.value = r.id;
      opt.textContent = `${r.name} (STL ${r.stl}, CHA ${r.cha})`;
      select.appendChild(opt);
    });
    content.appendChild(select);

    const preview = document.createElement('div');
    preview.id = 'rescue-preview';
    preview.style.cssText = 'font-size:11px;color:var(--text-lo,#888);margin-bottom:6px';
    const updatePreview = () => {
      const selRunner = opts.rescuerPool.find(r => r.id === select.value);
      if (!selRunner) {
        preview.textContent = '';
        return;
      }
      const score = Math.floor((selRunner.stl + selRunner.cha) / 2) + 10; // expected d20
      const chance = Math.max(0, Math.min(100, Math.round(((score - dc + 10) / 20) * 100)));
      preview.textContent = t('rescue.chance_preview', { dc: String(dc), chance: String(chance) });
    };
    select.addEventListener('change', updatePreview);
    updatePreview();
    content.appendChild(preview);

    const cost = document.createElement('div');
    cost.style.cssText = 'font-size:11px;color:var(--amber,#fa3)';
    cost.textContent = t('rescue.prep_cost_note', { amount: '2,000' });
    content.appendChild(cost);
  };

  // --- Bribe tab content ---
  const renderBribeTab = () => {
    content.innerHTML = '';
    if (isFederal) {
      const blocked = document.createElement('div');
      blocked.style.cssText = 'color:var(--red,#e63946);font-size:12px;padding:20px;text-align:center';
      blocked.textContent = t('rescue.bribe_federal_blocked');
      content.appendChild(blocked);
      return;
    }
    const info2 = document.createElement('div');
    info2.style.cssText = 'font-size:12px;color:var(--text-mid,#bbb);margin-bottom:10px';
    info2.textContent = t('rescue.bribe_description');
    content.appendChild(info2);
  };

  renderStealthTab();

  let currentMode: 'stealth' | 'bribe' = 'stealth';
  stealthTab.addEventListener('click', () => {
    currentMode = 'stealth';
    stealthTab.classList.add('active');
    stealthTab.style.color = 'var(--cyan,#0f5)';
    stealthTab.style.borderBottom = '2px solid var(--cyan,#0f5)';
    bribeTab.classList.remove('active');
    bribeTab.style.color = 'var(--text-lo,#888)';
    bribeTab.style.borderBottom = '2px solid transparent';
    renderStealthTab();
  });
  bribeTab.addEventListener('click', () => {
    if (bribeTab.disabled) return;
    currentMode = 'bribe';
    bribeTab.classList.add('active');
    bribeTab.style.color = 'var(--cyan,#0f5)';
    bribeTab.style.borderBottom = '2px solid var(--cyan,#0f5)';
    stealthTab.classList.remove('active');
    stealthTab.style.color = 'var(--text-lo,#888)';
    stealthTab.style.borderBottom = '2px solid transparent';
    renderBribeTab();
  });

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:10px;justify-content:flex-end';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'rescue-cancel';
  cancelBtn.textContent = t('rescue.btn_cancel');
  cancelBtn.style.cssText = 'background:transparent;border:1px solid var(--border,#333);color:var(--text-mid,#bbb);padding:8px 16px;cursor:pointer;font-family:inherit;border-radius:3px';
  btnRow.appendChild(cancelBtn);

  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'rescue-confirm';
  confirmBtn.textContent = t('rescue.btn_confirm');
  confirmBtn.style.cssText = 'background:var(--cyan,#0f5);border:0;color:#000;padding:8px 16px;cursor:pointer;font-family:inherit;font-weight:bold;border-radius:3px';
  btnRow.appendChild(confirmBtn);

  panel.appendChild(btnRow);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  activeOverlay = overlay;

  // Focus-trap: confirm button takes focus.
  setTimeout(() => confirmBtn.focus(), 0);

  const close = () => {
    _resetRescueModal();
    document.removeEventListener('keydown', onKeydown);
  };
  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      confirmBtn.focus();
    }
  };
  document.addEventListener('keydown', onKeydown);

  overlay.addEventListener('click', (e: MouseEvent) => {
    if (e.target === overlay) close();
  });

  cancelBtn.addEventListener('click', close);
  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true;
    confirmBtn.textContent = '...';
    try {
      await opts.onSubmit(currentMode);
    } catch (err) {
      console.error('[rescue-modal] submit failed:', err);
    } finally {
      close();
    }
  });
}
